import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { EntityTypeQueries } from '@admin/data'
import { EntityStorageType, EntityType } from '@portal/pkg-api/gen/navigator'
import { Box, Stack, Typography } from '@mui/material'
import { EntityTypeAvatar } from '@portal/pkg-filter-criteria'
import { useTranslation } from 'react-i18next'
import { NavTab, NavTabs } from '@shared/components'
import { zodValidator } from '@tanstack/zod-adapter'
import { EntityTypeFeature } from '@admin/model'
import { useState } from 'react'
import { EntityTypeDetailValidContext } from '@admin/preferences/helper/EntityTypeDetailValidContext'

export const Route = createFileRoute('/_auth/preferences/entityTypes/$entityTypeId')({
  component: RouteComponent,
  params: zodValidator(
    z.object({
      entityTypeId: z.union([z.coerce.number(), z.literal('new')]),
    }),
  ),
  beforeLoad: async ({ params: { entityTypeId }, context: { queryClient } }) => {
    if (entityTypeId === 'new') {
      return { entityType: createNewEntityDefinition() }
    }

    const entityTypes = await queryClient.ensureQueryData(EntityTypeQueries.entityTypes())
    const entityType = entityTypes.find((entityType) => entityType.id === entityTypeId)
    if (!entityType) {
      throw redirect({ from: Route.fullPath, to: '..' })
    }
    return { entityType }
  },
})

function RouteComponent() {
  const { t } = useTranslation('admin')
  const { entityTypeId } = Route.useParams()
  const { entityType } = Route.useRouteContext()

  const [valid, setValid] = useState(true)

  const hasStages = entityType.features.includes(EntityTypeFeature.STAGES)
  const hasTimeSeriesSubEntity = entityType.features.includes(
    EntityTypeFeature.TIME_SERIES_SUB_ENTITY,
  )
  const isEaTEntityType = entityType.storageType === EntityStorageType.TABULAR

  const isCreate = entityTypeId === 'new'

  return (
    <EntityTypeDetailValidContext.Provider value={[valid, setValid]}>
      <Box sx={{ width: '100%', pl: 2 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="h5">
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
              }}
            >
              {entityTypeId !== 'new' && (
                <Box component="span" sx={{ mr: 1 }}>
                  <EntityTypeAvatar entityType={entityType} size={22} />
                </Box>
              )}
              {entityTypeId === 'new'
                ? t('entityType.createTitle')
                : t('entityType.title', { name: entityType.name })}
            </Stack>
          </Typography>
        </Stack>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mr: 2 }}>
          <NavTabs>
            <NavTab
              value="basic"
              to={Route.fullPath}
              params={{ entityTypeId }}
              activeOptions={{ exact: true }}
              label={t('entityType.tabs.labels.basic')}
              disabled={!valid}
            />
            <NavTab
              value="fields"
              to="/preferences/entityTypes/$entityTypeId/fields"
              params={{ entityTypeId }}
              label={t('entityType.tabs.labels.fields')}
              disabled={!valid || isCreate}
            />
            <NavTab
              value="stages"
              to="/preferences/entityTypes/$entityTypeId/stages"
              params={{ entityTypeId }}
              label={t('entityType.tabs.labels.stages')}
              disabled={!valid || isCreate || !hasStages}
            />
            <NavTab
              value="recurrences"
              to="/preferences/entityTypes/$entityTypeId/recurrences"
              params={{ entityTypeId }}
              label={t('entityType.tabs.labels.recurrences')}
              disabled={!valid || isCreate || !hasStages || isEaTEntityType}
            />
            <NavTab
              value="timeSeriesSubEntity"
              to="/preferences/entityTypes/$entityTypeId/timeSeriesSubEntity"
              params={{ entityTypeId }}
              label={t('entityType.tabs.labels.timeSeriesSubEntity')}
              disabled={!valid || isCreate || !hasTimeSeriesSubEntity}
            />
            <NavTab
              value="rules"
              to="/preferences/entityTypes/$entityTypeId/rules"
              params={{ entityTypeId }}
              label={t('entityType.tabs.labels.rules')}
              disabled={!valid || isCreate || hasTimeSeriesSubEntity}
            />
          </NavTabs>
        </Box>
        <Box sx={{ p: 2, height: 'calc(100vh - 135px)', overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </EntityTypeDetailValidContext.Provider>
  )
}

function createNewEntityDefinition(): EntityType {
  return {
    id: -1,
    name: '',
    code: '',
    iconUrl: '',
    storageType: EntityStorageType.EAV,
    effectiveColor: '',
    features: [],
    successors: [],
    entityTypeFeatures: [],
    calculateOnCreate: false,
    requiresSubEntity: false,
    enabled: true,
    version: 0,
  }
}
